import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCompanies } from '../features/company/CompanyContext';
import { COMPANY_STATUS } from '../lib/format';
import { IconCheck, IconPlus, IconSwap } from './NavIcons';
import CompanyLogo from './CompanyLogo';

export default function CompanySwitcher() {
  const { companies, active, select, loading } = useCompanies();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (loading) return <div className="h-10 w-10 animate-pulse rounded-xl bg-[#fdf2f8] sm:w-[200px]" />;

  if (!companies.length) {
    return (
      <Link
        to="/company"
        title="Register your company"
        className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-[#f9a8d4] px-3 py-2 text-xs font-bold text-[#d6006e] hover:bg-[#fdf2f8]"
      >
        <IconPlus className="h-4 w-4" />
        <span className="hidden sm:inline">Register your company</span>
      </Link>
    );
  }

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${active?.logicalName || active?.name || 'Company'} — switch company`}
        className="flex min-w-0 max-w-[260px] items-center gap-2 rounded-xl bg-white p-1.5 text-left text-[#6b0030] transition-colors hover:bg-[#fdf2f8] sm:pr-2.5"
        style={{ border: '1px solid #fce7f3' }}
      >
        {active && <CompanyLogo companyId={active.id} name={active.logicalName || active.name} hasLogo={active.hasLogo} className="h-7 w-7" />}
        <span className="hidden min-w-0 flex-1 sm:block">
          <span className="block truncate text-sm font-bold leading-tight">{active?.logicalName || active?.name}</span>
          <span className="block truncate text-[10px] font-semibold uppercase tracking-wider text-[#b0708f]">
            {active?.workspaceLabel || active?.classification?.type.label || 'Company workspace'}
          </span>
        </span>
        <IconSwap className="hidden h-4 w-4 shrink-0 text-[#c084a0] sm:block" />
      </button>

      {open && (
        <div
          role="menu"
          className="mz-pop absolute right-0 top-full z-50 mt-2 w-[300px] overflow-hidden rounded-2xl bg-white text-[#6b0030]"
          style={{ border: '1px solid #fce7f3', boxShadow: '0 24px 60px rgba(43,0,24,0.18)' }}
        >
          <p className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#c084a0]">Your companies · {companies.length}</p>
          <div className="max-h-72 overflow-y-auto p-1.5">
            {companies.map((c) => {
              const isActive = c.id === active?.id;
              const status = COMPANY_STATUS[c.status];
              return (
                <button
                  key={c.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => {
                    setOpen(false);
                    if (!isActive) select(c.id);
                  }}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors ${isActive ? 'bg-[#fdf2f8]' : 'hover:bg-[#fff5f8]'}`}
                >
                  <CompanyLogo companyId={c.id} name={c.logicalName || c.name} hasLogo={c.hasLogo} className="h-8 w-8" />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm ${isActive ? 'font-black text-[#d6006e]' : 'font-bold'}`}>{c.logicalName || c.name}</span>
                    <span className="block truncate text-[11px] text-[#b0708f]">
                      {[c.workspaceLabel, c.targetLender && `for ${c.targetLender}`, status?.label].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {c.latestReport && (
                    <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-[10px] font-black text-[#d6006e]" style={{ border: '1px solid #fce7f3' }}>
                      {c.latestReport.grade}
                    </span>
                  )}
                  <IconCheck className={`h-4 w-4 shrink-0 text-[#d6006e] ${isActive ? '' : 'invisible'}`} />
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-1.5 border-t border-[#fce7f3] p-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/');
              }}
              className="rounded-xl px-3 py-2 text-xs font-bold text-[#6b0030] hover:bg-[#fdf2f8]"
            >
              Manage
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/company/new');
              }}
              className="inline-flex items-center justify-center gap-1 rounded-xl bg-[#d6006e] px-3 py-2 text-xs font-bold text-white"
            >
              <IconPlus className="h-3.5 w-3.5" /> Add company
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
